import analytics from '../../utils/analytics';
import camelCase from 'lodash/camelCase';
import {
    resolveAnalyticsUrl,
    extractContentfulEntryId
} from '../../utils/content-analytics-resources';
import { type ParentPageType } from '../../utils/parent-page-type';

/**
 * Required fields for tracking content link analytics events.
 * All fields are mandatory when providing analytics data.
 */
export interface ContentLinkAnalyticsPayload {
    /** The type of link being clicked ('card' or 'text-link') */
    linkType: 'card' | 'text-link';
    /** The context where the link appears. */
    context: 'search-results' | 'sidebar' | 'browse' | 'reference';
    /** The position/index of the link in the list (1-based) */
    position: number;
    /** The content ID/ARI of the linked content */
    linkContentAri: string;
    /** The resolved URL of the linked content */
    linkContentResolvedUrl: string;
    /** The type of parent page (optional, can be null for home/root pages) */
    parentPageType?: ParentPageType;
}

export interface ContentLinkAnalytics {
    payload?: ContentLinkAnalyticsPayload;
    /** Should skip the submission of the content analytics */
    shouldSkip: boolean;
}
/**
 * Builds enhanced analytics properties for content links
 */
function buildContentLinkAnalytics(
    href: string | null,
    contentLinkAnalytics: ContentLinkAnalyticsPayload
) {
    const {
        linkType,
        context,
        position,
        linkContentAri,
        linkContentResolvedUrl,
        parentPageType
    } = contentLinkAnalytics;

    const linkContentEntryId = extractContentfulEntryId(linkContentAri);
    const resolvedUrl = resolveAnalyticsUrl(linkContentResolvedUrl);

    return {
        event: 'clicked',
        eventComponent: 'content-link',
        eventContainer: context,
        position,
        link_type: linkType,
        link_content_ari: linkContentAri,
        link_content_entry_id: linkContentEntryId,
        link_content_resolved_url: resolvedUrl,
        ...(parentPageType && { parent_page_type: parentPageType }),
        clickTarget: href
    };
}
/**
 * Builds standard analytics properties for regular links
 */
function buildStandardAnalytics(href: string | null, name: string) {
    let target;
    let actionSubjectId;
    if (name.match(/.*button/i)) {
        target = 'button';
        actionSubjectId = camelCase(name.replace(/button/i, ''));
    } else if (name.match(/.*card/i)) {
        target = 'card';
        actionSubjectId = camelCase(name.replace(/card/i, ''));
    } else {
        target = 'link';
        actionSubjectId = camelCase(name);
    }
    return { target, actionSubjectId, clickTarget: href };
}

export function trackLink(params: {
    href: string | null;
    text?: string;
    name?: string;
    contentLinkAnalytics?: ContentLinkAnalytics;
}) {
    const { href, text, name, contentLinkAnalytics } = params;

    // Only track content link analytics if payload exists and shouldSkip is false
    if (contentLinkAnalytics?.payload && !contentLinkAnalytics?.shouldSkip) {
        const enhancedProperties = buildContentLinkAnalytics(
            href,
            contentLinkAnalytics?.payload
        );

        if (name) {
            const { target, actionSubjectId } = buildStandardAnalytics(
                href,
                name
            );
            return analytics.trackEvent('content-link clicked', {
                ...enhancedProperties,
                actionSubjectId,
                target
            });
        }

        return analytics.trackEvent('content-link clicked', enhancedProperties);
    }
    if (name) {
        const { target, actionSubjectId, clickTarget } = buildStandardAnalytics(
            href,
            name
        );
        return analytics.processEvent(target, '', {
            actionSubjectId,
            clickTarget
        });
    }

    analytics.trackLinkClick(href, text);
}
// Backward compatible wrapper
const track = (href: string | null, text?: string, name?: string) => {
    trackLink({ href, text, name });
};

export default track;
